import { eexCapabilitiesFor } from "../../../lib/eex-access";
import type { AuthUser } from "../../../stores/auth";
import type { EexStatsMetrics, EexStatsResponse, EexStatsQuery } from "@chro/shared";
import type { StatsMetrics, StatsMonth } from "./model";

export function statsMetrics(metrics: EexStatsMetrics): StatsMetrics {
  return {
    energyMwh: metrics.grossMWh, knownEnergyMwh: metrics.knownMWh, powerMwh: metrics.powerMWh, gasMwh: metrics.gasMWh,
    buyMwh: metrics.buyMWh, sellMwh: metrics.sellMWh, powerBuyMwh: metrics.powerBuyMWh, gasBuyMwh: metrics.gasBuyMWh,
    executions: metrics.tradeCount, cleared: metrics.clearedCount, bilateral: metrics.bilateralCount,
    missingVolumeCount: metrics.missingVolumeCount, costsReason: metrics.performance.coverage.tradingCostsEUR.reason,
    pnlReason: metrics.performance.coverage.recognizedPnlEUR.reason, mtmReason: metrics.performance.coverage.openMtmEUR.reason,
    pnlEur: metrics.performance.recognizedPnlEUR, costsEur: metrics.performance.tradingCostsEUR, mtmEur: metrics.performance.openMtmEUR,
  };
}
export function statsPresentation(data: EexStatsResponse) {
  return {
    summary: statsMetrics(data.summary),
    months: data.monthly.map((month): StatsMonth => ({ month: month.month, powerMwh: month.powerMWh, gasMwh: month.gasMWh, executions: month.tradeCount, powerExecutions: month.powerCount, gasExecutions: month.gasCount, pnlEur: month.performance.recognizedPnlEUR })),
    books: data.books.map(book => ({ id: book.id, label: book.label, metrics: statsMetrics(book), monthlyPerformance: book.monthlyPerformance.map(month => ({ month: month.month, pnlEur: month.recognizedPnlEUR })) })),
    traders: data.traders.map(trader => ({ id: trader.id, label: trader.label, masked: trader.identityMasked, metrics: statsMetrics(trader), monthlyPerformance: trader.monthlyPerformance.map(month => ({ month: month.month, pnlEur: month.recognizedPnlEUR })) })),
    cross: data.bookTraders.map(cell => ({ bookId: cell.book === null ? "unbooked" : `book:${cell.book}`, traderId: cell.traderId, metrics: statsMetrics(cell), monthlyPerformance: cell.monthlyPerformance.map(month => ({ month: month.month, pnlEur: month.recognizedPnlEUR })) })),
    allocations: data.allocation.flatMap(edge => edge.commodity === "unknown" ? [] : [{ commodity: edge.commodity, bookId: edge.book === null ? "unbooked" : `book:${edge.book}`, traderId: edge.traderId, mwh: edge.knownMWh }]),
  };
}

export function statsRequestPath(query: EexStatsQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, value);
  return `/eex/stats?${params.toString()}`;
}
export function statsQueryKey(companyId: string | null, user: AuthUser | null, requestPath: string | null) {
  return ["eex-stats", companyId, user?.userId, user?.role, user ? eexCapabilitiesFor(user) : null, requestPath] as const;
}
export function focusedRange(range: { from: string; to: string }, month?: string) {
  if (!month) return range;
  const [year, number] = month.split("-").map(Number);
  const from = `${month}-01`, to = new Date(Date.UTC(year!, number!, 0)).toISOString().slice(0, 10);
  return { from: from < range.from ? range.from : from, to: to > range.to ? range.to : to };
}
